import { action, computed, observable, makeObservable } from 'mobx';

import DataMixin from 'utils/stores/DataMixin';

import API from 'utils/api';
import RootStore from 'stores/Root';

import { UnitResponse } from 'utils/api/modules/units/units.types';

import { OrgUnit, isIntake } from './types';

export default class OrganisationStructureStore extends DataMixin {
  @observable units: UnitResponse[] = [];

  /** Only the newest load may write: an earlier one landing last would resurrect deleted units. */
  private loadSequence = 0;

  constructor(rootStore: RootStore, api: typeof API) {
    super(rootStore, api);

    makeObservable(this);
  }

  /** The flat list as a tree. Read in render — never through useMemo, which would break reactivity. */
  @computed get tree(): OrgUnit[] {
    const nodes = new Map<string, OrgUnit>(
      this.units.map((unit) => [unit.uuid, { ...unit, children: [] as OrgUnit[] }])
    );

    const roots: OrgUnit[] = [];
    this.units.forEach((unit) => {
      const node = nodes.get(unit.uuid) as OrgUnit;
      const parent = unit.parent ? nodes.get(unit.parent) : undefined;
      if (parent) {
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    });

    // Intake is a system unit and belongs under the customer's own ones, wherever it was created.
    nodes.forEach((node) => node.children.sort((a, b) => Number(isIntake(a)) - Number(isIntake(b))));

    return roots;
  }

  /** What the page renders: the tree, a skeleton, or a retry row. */
  @computed get surfaceState(): 'loading' | 'error' | 'default' {
    // No root means the tenant was never seeded, and every write would be refused, so it is
    // the retry row rather than a tree with nothing in it.
    if (this.isFailure || (this.isLoaded && !this.units.length)) return 'error';
    if (!this.isLoaded) return 'loading';
    return 'default';
  }

  @action.bound
  async loadUnits(showLoading = true): Promise<void> {
    // A reload after a write keeps the tree on screen: blanking it to the skeleton for the
    // round trip loses the selection and the Add button.
    if (showLoading) this.isLoaded = false;
    this.isFailure = false;

    this.loadSequence += 1;
    const sequence = this.loadSequence;
    try {
      const { data } = await this.api.units.loadUnits()();
      if (sequence === this.loadSequence) this.setUnits(data);
    } catch {
      if (sequence === this.loadSequence) this.setFailed();
    } finally {
      if (sequence === this.loadSequence) this.setLoaded();
    }
  }

  // Every write reloads rather than patching locally: a move or a delete changes rows the
  // response does not carry.
  @action.bound
  async createUnit(parent: string, name: string): Promise<void> {
    await this.api.units.createUnit({ parent, name })();
    await this.loadUnits(false);
  }

  @action.bound
  async renameUnit(uuid: string, name: string): Promise<void> {
    await this.api.units.updateUnit(uuid, { name })();
    await this.loadUnits(false);
  }

  @action.bound
  async moveUnit(uuid: string, parent: string): Promise<void> {
    await this.api.units.moveUnit(uuid, parent)();
    await this.loadUnits(false);
  }

  @action.bound
  async deleteUnit(uuid: string, destination: string): Promise<void> {
    await this.api.units.deleteUnit(uuid, destination)();
    await this.loadUnits(false);
  }

  @action.bound
  setUnits(units: UnitResponse[]): void {
    this.units = units;
  }

  @action.bound
  setFailed(): void {
    this.isFailure = true;
  }

  @action.bound
  setLoaded(): void {
    this.isLoaded = true;
  }
}
